import { Grid } from "@mui/material";
import { memo, useCallback } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import biApi from "../../../../../api/biApi.ts";
import type { SmartViewWidget } from "../../../../../api/types/dashboardsTypes.ts";
import type { ReportField } from "../../../../../api/types/svTypes.ts";
import useCommonReportHandlers from "../../../reporting/sv/components/builder/common/hooks/useCommonReportHandlers.ts";
import type { ReportConfigurationForRunning } from "../../../reporting/sv/components/viewer/storage/runState.ts";
import { useAppDispatch } from "../../../reporting/sv/store/hooks/useAppDispatch.ts";
import { changeDataSet } from "../../../reporting/sv/store/thunks/metaDataThunks.ts";
import { createCurrentReportAction } from "../../../reporting/sv/store/utils/storeUtils.ts";
import SkeletonLoader from "../../components/SkeletonLoader.tsx";
import { useDashboardContext } from "../../contexts/DashboardContext.tsx";
import SmartViewChartWidget from "./chart/SmartViewChartWidget.tsx";
import type { DrillDownArgs } from "./DrillDownArgs.ts";
import SmartViewPivotWidget from "./pivot/SmartViewPivotWidget.tsx";
import SmartViewTabularWidget from "./tabular/SmartViewTabularWidget.tsx";
import SmartViewTileWidget from "./tile/SmartViewTileWidget.tsx";
import {
  isChartReportConfiguration,
  isPivotReportConfiguration,
  isTabularReportConfiguration,
  isTileReportConfiguration,
} from "./utils/reportConfigurationHelper.ts";

interface SmartViewWidgetProps {
  widget: SmartViewWidget;
}

const SmartViewWidgetComponent = ({ widget }: SmartViewWidgetProps) => {
  const { updateTile } = useDashboardContext();

  const fetchRunningData = useCallback(
    (signal?: AbortSignal) => {
      return biApi.dashboardReports.getReportConfigurationForRunning(widget.reportId, signal);
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [widget.reportId, widget.etag]
  );

  const [result, error, { isFetching }] = useFetch(fetchRunningData, {
    onSetData: () => updateTile({ id: widget.widgetId, isLoaded: true }),
  });

  if (error) {
    return <DataLoadingFailed title="Could not load report" />;
  }

  if (isFetching || !result) {
    return <SkeletonLoader />;
  }

  return <SmartViewWidgetLoaded widget={widget} result={result} />;
};

const SmartViewWidgetLoaded = memo(function SmartViewWidgetLoaded({
  widget,
  result,
}: {
  widget: SmartViewWidget;
  result: ReportConfigurationForRunning;
}) {
  const dispatch = useAppDispatch();

  const { onDrillDown } = useCommonReportHandlers(getAggregationFields(result));

  const handleDrillDown = useCallback(
    (args: DrillDownArgs) => {
      if (result?.dataSetId) {
        dispatch(changeDataSet(result.dataSetId));
      }
      dispatch(createCurrentReportAction("setEtag", { etag: args.etag, cacheValid: undefined }));
      dispatch(createCurrentReportAction("setSessionId", args.sessionId));

      onDrillDown(args.field, args.cell, { reportId: widget.reportId });
    },
    [dispatch, onDrillDown, result, widget.reportId]
  );

  if (isTabularReportConfiguration(result)) {
    return <SmartViewTabularWidget data={result} widget={widget} onDrillDown={handleDrillDown} />;
  }

  if (isPivotReportConfiguration(result)) {
    return <SmartViewPivotWidget data={result} widget={widget} onDrillDown={handleDrillDown} />;
  }

  if (isChartReportConfiguration(result)) {
    return <SmartViewChartWidget data={result} widget={widget} onDrillDown={handleDrillDown} />;
  }

  if (isTileReportConfiguration(result)) {
    return <SmartViewTileWidget data={result} widget={widget} />;
  }

  return (
    <Grid container spacing={2} sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
      Widget with type {widget.type}is not supported. WidgetId: {widget.widgetId}
    </Grid>
  );
});

export default SmartViewWidgetComponent;

function getAggregationFields(result: ReportConfigurationForRunning): ReportField[] {
  if (isTabularReportConfiguration(result) || isTileReportConfiguration(result)) {
    return result.fields;
  }

  if (isPivotReportConfiguration(result) || isChartReportConfiguration(result)) {
    return result.values;
  }

  return [];
}
